<template>
  <transition name="callbar">
    <div
      v-if="call"
      class="active-call-bar"
      :class="`active-call-bar--${tone}`"
      :data-call-phase="phase"
      role="status"
      aria-live="polite"
    >
      <div class="active-call-bar__inner">
        <v-icon
          class="active-call-bar__icon"
          :class="{ 'active-call-bar__icon--pulse': isWaiting }"
          :color="iconColor"
        >{{ icon }}</v-icon>

        <div class="active-call-bar__info">
          <div class="active-call-bar__label">{{ call.contactLabel }}</div>
          <div class="active-call-bar__sub">
            {{ call.phoneTo }} ·
            <span class="active-call-bar__status">{{ $gt(phaseLabel) }}</span>
            <span v-if="phase === 'connected'" class="active-call-bar__timer">{{ connectedFor }}</span>
          </div>
          <!-- Kundenfeedback Kanzlei Polz 27.10.2026 (I.4): Im Callback-Modus
               klingelt zuerst das eigene Telefon – sonst wartet man auf ein
               Freizeichen, das es auf dem Rechner nie gibt. -->
          <div v-if="ownPhoneHint" class="active-call-bar__hint">
            {{ $gt('Nach dem Abheben wählt KARS automatisch:') }}
            {{ call.contactLabel }}
          </div>
        </div>

        <v-btn
          :icon="true"
          variant="flat"
          :color="isFinal ? 'default' : 'error'"
          size="small"
          @click="onHangup"
          :aria-label="isFinal ? $gt('Anzeige schließen') : $gt('Anruf beenden')"
        >
          <v-icon>{{ isFinal ? 'mdi-close' : $gt('mdi-phone-hangup') }}</v-icon>
        </v-btn>
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { useCallStore } from "@/stores/callStore";
import {
  CALL_PHASE_LABELS,
  deriveCallPhase,
  formatCallDuration,
  isTerminalCallPhase,
  showsOwnPhoneHint,
  type CallPhase,
} from "@/utils/callPhase";

const callStore = useCallStore();
const call = computed(() => callStore.activeCall);

// Tick a reactive counter every second so the elapsed time re-renders.
const now = ref(Date.now());
let timer: number | null = null;

function startTimer() {
  if (timer !== null) return;
  timer = window.setInterval(() => {
    now.value = Date.now();
  }, 1000);
}

function stopTimer() {
  if (timer !== null) {
    window.clearInterval(timer);
    timer = null;
  }
}

watch(
  () => !!call.value,
  (active) => (active ? startTimer() : stopTimer()),
  { immediate: true }
);

onBeforeUnmount(stopTimer);

// Anrufphase aus Twilio-Status (eigenes Telefon + angerufener Kontakt).
// Kundenfeedback Kanzlei Polz 27.10.2026 (I.4).
const phase = computed<CallPhase>(() => (call.value ? deriveCallPhase(call.value) : "connecting"));
const phaseLabel = computed(() => CALL_PHASE_LABELS[phase.value]);
const isFinal = computed(() => isTerminalCallPhase(phase.value));
const isWaiting = computed(() =>
  ["connecting", "own-phone-ringing", "dialing", "ringing"].includes(phase.value)
);
const ownPhoneHint = computed(() => !!call.value && showsOwnPhoneHint(call.value, phase.value));

type CallTone = "waiting" | "connected" | "problem" | "ended";
const tone = computed<CallTone>(() => {
  switch (phase.value) {
    case "connected":
      return "connected";
    case "busy":
    case "no-answer":
    case "failed":
    case "own-phone-missed":
      return "problem";
    case "canceled":
    case "ended":
      return "ended";
    default:
      return "waiting";
  }
});

const icon = computed(() => {
  switch (tone.value) {
    case "connected":
      return "mdi-phone-in-talk";
    case "problem":
      return "mdi-phone-missed";
    case "ended":
      return "mdi-phone-hangup";
    default:
      return "mdi-phone-outgoing";
  }
});

const iconColor = computed(() => {
  switch (tone.value) {
    case "connected":
      return "success";
    case "problem":
      return "error";
    case "ended":
      return "grey";
    default:
      return "primary";
  }
});

// „Verbunden 00:12“ – gezählt ab dem Abheben des Kontakts, nicht ab dem Klick.
const connectedFor = computed(() => {
  const since = call.value?.connectedAt;
  if (!since) return "";
  return formatCallDuration(now.value - since);
});

function onHangup() {
  // Full server-side hangup is backend mode 2 (browser SDK). For callback
  // mode the user simply hangs up on their own phone; we just clear the UI.
  callStore.endCall();
}
</script>

<style scoped>
.active-call-bar {
  position: fixed;
  left: 50%;
  bottom: 24px;
  transform: translateX(-50%);
  z-index: 10000;
  min-width: 320px;
  max-width: 90vw;
  background: rgb(var(--v-theme-surface));
  color: rgb(var(--v-theme-on-surface));
  border-radius: 999px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.18);
  padding: 8px 16px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
}
.active-call-bar__inner {
  display: flex;
  align-items: center;
  gap: 12px;
}
.active-call-bar__icon {
  font-size: 28px;
}
.active-call-bar__info {
  flex: 1;
  min-width: 0;
}
.active-call-bar__label {
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.active-call-bar__sub {
  font-size: 0.8em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: rgba(var(--v-theme-on-surface), 0.7);
}
/* Status ist die eigentliche Nachricht der Leiste – hervorgehoben. */
.active-call-bar__status {
  font-weight: 600;
  color: rgb(var(--call-tone-color));
}
.active-call-bar__timer {
  margin-left: 4px;
  font-variant-numeric: tabular-nums;
  color: rgb(var(--call-tone-color));
}
.active-call-bar__hint {
  margin-top: 2px;
  font-size: 0.75em;
  line-height: 1.3;
  white-space: normal;
  color: rgba(var(--v-theme-on-surface), 0.75);
}

/* Phasenfarbe als Rahmen und im Statustext. */
.active-call-bar--waiting { --call-tone-color: var(--v-theme-primary); }
.active-call-bar--connected { --call-tone-color: var(--v-theme-success); }
.active-call-bar--problem { --call-tone-color: var(--v-theme-error); }
.active-call-bar--ended { --call-tone-color: var(--v-theme-on-surface); }
.active-call-bar {
  border-color: rgba(var(--call-tone-color, var(--v-theme-primary)), 0.45);
  transition: border-color 0.2s ease;
}

/* Wartephasen: Hörer pulsiert dezent, solange es klingelt / verbunden wird. */
.active-call-bar__icon--pulse {
  animation: callbar-pulse 1.4s ease-in-out infinite;
}
@keyframes callbar-pulse {
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.12); opacity: 0.65; }
}
@media (prefers-reduced-motion: reduce) {
  .active-call-bar__icon--pulse { animation: none; }
}

.callbar-enter-active,
.callbar-leave-active {
  transition: transform 0.2s ease, opacity 0.2s ease;
}
.callbar-enter-from,
.callbar-leave-to {
  opacity: 0;
  transform: translate(-50%, 10px);
}
</style>
